// GTM is a marketing-tag loader only. Product analytics stays in Firebase Analytics.
// Do not push user ids, PHI, or user properties into dataLayer.

import createLogger from '@/utils/logger'

const logger = createLogger('utils/gtm.ts')

type GtmConsentValue = 'granted' | 'denied'

export type GtmDataLayerEvent = {
  event: string
  [key: string]: unknown
}

declare global {
  interface Window {
    dataLayer?: unknown[]
    gtag?: (...args: unknown[]) => void
  }
}

export function isGTMEnabled(): boolean {
  const containerId = import.meta.env.VITE_GTM_CONTAINER_ID
  return Boolean(containerId && containerId.trim() !== '')
}

function dataLayer(): unknown[] | null {
  if (typeof window === 'undefined') return null
  if (!Array.isArray(window.dataLayer)) {
    window.dataLayer = []
  }
  return window.dataLayer
}

/**
 * Grant or revoke GTM ad consent. analytics_storage stays denied so GTM cannot
 * become a second GA4 property. Call only with privacyPolicy — never with PII.
 */
export function syncGtmAdConsent(privacyPolicyAccepted: boolean): void {
  if (!isGTMEnabled()) return

  const adConsent: GtmConsentValue = privacyPolicyAccepted ? 'granted' : 'denied'

  if (typeof window !== 'undefined') {
    window.gtag =
      window.gtag ||
      function gtag(...args: unknown[]) {
        if (!Array.isArray(window.dataLayer)) {
          window.dataLayer = []
        }
        // Same contract as Google's `dataLayer.push(arguments)` shim, via rest params.
        window.dataLayer.push(args)
      }
    window.gtag('consent', 'update', {
      ad_storage: adConsent,
      ad_user_data: adConsent,
      ad_personalization: adConsent,
      analytics_storage: 'denied',
    })
  }

  logger.debug('GTM ad consent updated:', adConsent)
}

/** Push a marketing event. Rejects payloads that look like identity or PHI. */
export function pushToDataLayer(data: GtmDataLayerEvent): void {
  if (!isGTMEnabled()) return

  const blockedKeys = [
    'userid',
    'uid',
    'email',
    'emailaddress',
    'gender',
    'stateofresidence',
    'dateofbirth',
    'dob',
    'birthdate',
    'phone',
    'phonenumber',
    'telephone',
    'firstname',
    'lastname',
    'fullname',
  ]
  const findBlockedKeys = (obj: unknown): string[] => {
    if (!obj || typeof obj !== 'object') return []
    const found = new Set<string>()
    const visited = new WeakSet<object>()
    const search = (current: unknown) => {
      if (!current || typeof current !== 'object') return
      if (visited.has(current)) return
      visited.add(current)
      for (const key of Object.keys(current)) {
        const normalizedKey = key.toLowerCase().replace(/[^a-z0-9]/g, '')
        if (blockedKeys.includes(normalizedKey)) {
          found.add(key)
        }
        const value = (current as Record<string, unknown>)[key]
        if (typeof value === 'object' && value !== null) {
          search(value)
        }
      }
    }
    search(obj)
    return Array.from(found)
  }

  const usedBlocked = findBlockedKeys(data)
  if (usedBlocked.length > 0) {
    logger.warn('Refused dataLayer push with blocked keys:', usedBlocked)
    return
  }

  const layer = dataLayer()
  if (!layer) return
  layer.push(data)
}
